import _ from 'lodash'
import PropTypes from 'prop-types'
import { ADMIN_ACTIONS, CURRENT_ACTIONS } from '../actions/types'

/**
 * @typedef {Object} AgencyAdmin
 * @property {number} id - Agency admin's unique ID.
 * @property {number} admin_id - Associated admin ID.
 * @property {number} agency_id - Associated agency ID.
 * @property {boolean} is_default - Whether it's the default agency admin.
 * @property {string} agency_name - Name of the agency.
 */

export const agencyAdminPropTypes = PropTypes.shape({
  id: PropTypes.number,
  admin_id: PropTypes.number,
  agency_id: PropTypes.number,
  is_default: PropTypes.bool,
  agency_name: PropTypes.string
})

/**
 * @typedef {Object} OrganizationAdmin
 * @property {number} id - Organization admin's unique ID.
 * @property {string} organization_name - Name of the organization.
 * @property {number} organization_id - Associated organization ID.
 * @property {number} admin_id - Associated admin ID.
 * @property {boolean} is_default - Whether it's the default organization admin.
 * @property {string} admin_name - Admin's name.
 */

export const organizationAdminPropTypes = PropTypes.shape({
  id: PropTypes.number,
  organization_name: PropTypes.string,
  organization_id: PropTypes.number,
  admin_id: PropTypes.number,
  is_default: PropTypes.bool,
  admin_name: PropTypes.string
})

/**
 * @typedef {Object} CurrentAdmin
 * @property {number} id - Admin's unique ID.
 * @property {string} name - Admin's name.
 * @property {string} email - Admin's email address.
 * @property {boolean|null} is_client_admin - Whether the admin is a client admin (nullable).
 * @property {number|null} client_id - Associated client ID (nullable).
 * @property {boolean} is_account_manager - Whether the admin is an account manager.
 * @property {boolean} show_ids - Whether to display IDs (custom flag).
 * @property {boolean|null|undefined} admin_has_users - Whether the admin has users (nullable, agency-dependent).
 * @property {number[]|null|undefined} role_ids - Array of role IDs (nullable, agency-dependent).
 * @property {string} authentication_token - Authentication token for the admin.
 * @property {Object} avatar - Admin's avatar with different sizes.
 * @property {string|null} avatar.url - URL of the main avatar (nullable).
 * @property {Object} avatar.small - Small-sized avatar.
 * @property {string|null} avatar.small.url - URL of the small avatar (nullable).
 * @property {Object} avatar.medium - Medium-sized avatar.
 * @property {string|null} avatar.medium.url - URL of the medium avatar (nullable).
 * @property {Object} avatar.initial - Initial-sized avatar.
 * @property {string|null} avatar.initial.url - URL of the initial avatar (nullable).
 * @property {boolean} enable_two_factor_auth - Whether 2FA is enabled.
 * @property {boolean} has_api_access - Whether the admin has API access.
 * @property {boolean} is_active - Whether the admin is active.
 * @property {string|null} mobile_for_2fa - Mobile number for 2FA (nullable).
 * @property {string|null} timezone - Admin's timezone (nullable).
 * @property {string} last_log_attempt_ip - Last login attempt IP address.
 * @property {string[]|null|undefined} roles_names - Array of role names (nullable, agency-dependent).
 * @property {string} verification_code - Verification code for 2FA.
 * @property {boolean} is_super_admin - Whether the admin is a super admin.
 * @property {boolean|null|undefined} linked_to_user - Whether the admin is linked to a user (nullable, agency-dependent).
 * @property {Array<any>|null|undefined} associated_portal_info - Info about associated portals (nullable, agency-dependent).
 * @property {string} featurebase_authentication_token - JWT for Featurebase authentication.
 * @property {any|null} user - Linked user object (nullable, shape depends on User model/serializer).
 * @property {'Agency' | 'Organization'} logged_in_as - Admin's current login context (e.g., "Organization" or "Agency").
 * @property {string[]|undefined} admin_permissions - Array of permissions for the admin (agency/organization-dependent).
 * @property {string} client_name - Name of the associated client.
 * @property {Array<any>} admin_clients - Array of associated admin clients (shape depends on AdminClient model/serializer).
 * @property {Array<AgencyAdmin>} agency_admins - Array of associated agency admins.
 * @property {Array<OrganizationAdmin>} organization_admins - Array of associated organization admins.
 * @property {Array<any>} client_account_managers - Array of associated client account managers (shape depends on ClientAccountManagerSerializer).
 */

export const currentAdminPropTypes = PropTypes.shape({
  if: PropTypes.number,
  name: PropTypes.string,
  email: PropTypes.string,
  is_client_admin: PropTypes.bool,
  client_id: PropTypes.number,
  is_account_manager: PropTypes.bool,
  show_ids: PropTypes.bool,
  admin_has_users: PropTypes.bool,
  role_ids: PropTypes.arrayOf(PropTypes.number),
  authentication_token: PropTypes.string,
  avatar: PropTypes.shape({
    url: PropTypes.string,
    small: PropTypes.shape({ url: PropTypes.string }),
    medium: PropTypes.shape({ url: PropTypes.string }),
    initial: PropTypes.shape({ url: PropTypes.string })
  }),
  enable_two_factor_auth: PropTypes.bool,
  has_api_access: PropTypes.bool,
  is_active: PropTypes.bool,
  mobile_for_2fa: PropTypes.string,
  timezone: PropTypes.string,
  last_log_attempt_ip: PropTypes.string,
  roles_names: PropTypes.arrayOf(PropTypes.string),
  verification_code: PropTypes.string,
  is_super_admin: PropTypes.bool,
  linked_to_user: PropTypes.bool,
  associated_portal_info: PropTypes.arrayOf(PropTypes.any),
  featurebase_authentication_token: PropTypes.string,
  user: PropTypes.any,
  logged_in_as: PropTypes.string,
  admin_permissions: PropTypes.oneOfType([PropTypes.bool, PropTypes.arrayOf(PropTypes.string)]),
  client_name: PropTypes.string,
  admin_clients: PropTypes.arrayOf(PropTypes.any),
  agency_admins: PropTypes.arrayOf(agencyAdminPropTypes),
  organization_admins: PropTypes.arrayOf(organizationAdminPropTypes),
  client_account_managers: PropTypes.arrayOf(PropTypes.any)
})

/**
 * @typedef {Object} AdminsState
 * @property {Array<CurrentAdmin>} admins - Array of admins.
 * @property {CurrentAdmin} current_admin - Current admin object.
 * @property {boolean} updatingPassword - Whether the admin is updating their password.
 */

export const adminsPropTypes = PropTypes.shape({
  admins: PropTypes.arrayOf(currentAdminPropTypes),
  current_admin: currentAdminPropTypes,
  updatingPassword: PropTypes.bool
})

/** @type {AdminsState} */
const initialState = {
  admins: [],
  current_admin: { id: null },
  updatingPassword: false
}

/**
 * Reducer to manage the admin state.
 *
 * @param {AdminsState} state
 * @param {{ type: string, payload: any }} action
 *
 * @returns {AdminsState}
 */
export default (state = initialState, { type, payload }) => {
  switch (type) {
    case ADMIN_ACTIONS.SET_CURRENT:
      return { ...state, current_admin: payload }

    case ADMIN_ACTIONS.FETCH:
      return { ...state, ...(payload ? { [payload.id]: payload } : {}) }

    case ADMIN_ACTIONS.CREATE:
    case ADMIN_ACTIONS.EDIT:
      return { ...state, [payload.id]: payload }

    case ADMIN_ACTIONS.DELETE:
      return _.omit(state, payload)

    case ADMIN_ACTIONS.UPDATE_PASSWORD:
      return { ...state, updatingPassword: payload }

    case ADMIN_ACTIONS.FORGOT_PASSWORD:
    case ADMIN_ACTIONS.RESET_PASSWORD:
    case ADMIN_ACTIONS.VERIFY_PASSWORD_TOKEN:
      return { ...state, ...payload }

    case CURRENT_ACTIONS.SET_AGENCY:
      return {
        ...state,
        current_admin: { ...state.current_admin, logged_in_as: 'Agency' }
      }

    case CURRENT_ACTIONS.SET_ORGANIZATION:
      return {
        ...state,
        current_admin: { ...state.current_admin, logged_in_as: 'Organization' }
      }

    default:
      return state
  }
}
